import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  Vendor,
  VendorType,
  VendorStatus,
  PurchaseOrder,
  PurchaseOrderStatus,
  VendorInvoice,
  InvoiceStatus,
} from '@/models/vendor.model';

const VENDORS_COLLECTION = 'vendors';
const PURCHASE_ORDERS_COLLECTION = 'purchaseOrders';
const VENDOR_INVOICES_COLLECTION = 'vendorInvoices';

export class VendorService {
  // ========== Vendor Management ==========

  static async getAllVendors(): Promise<Vendor[]> {
    const q = query(
      collection(db, VENDORS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToVendor(doc.id, doc.data()));
  }

  static async getVendorById(id: string): Promise<Vendor | null> {
    const docRef = doc(db, VENDORS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToVendor(docSnap.id, docSnap.data());
  }

  static async createVendor(vendorData: Partial<Vendor>): Promise<string> {
    const now = Timestamp.now();
    const data: DocumentData = {
      name: vendorData.name || '',
      type: vendorData.type || VendorType.other,
      status: vendorData.status || VendorStatus.pending,
      contactName: vendorData.contactName || null,
      email: vendorData.email || null,
      phone: vendorData.phone || null,
      address: vendorData.address || null,
      city: vendorData.city || null,
      state: vendorData.state || null,
      zipCode: vendorData.zipCode || null,
      country: vendorData.country || null,
      website: vendorData.website || null,
      taxId: vendorData.taxId || null,
      paymentTerms: vendorData.paymentTerms || null,
      rating: vendorData.rating || null,
      notes: vendorData.notes || null,
      tags: vendorData.tags || [],
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, VENDORS_COLLECTION), data);
    return docRef.id;
  }

  static async updateVendor(id: string, updates: Partial<Vendor>): Promise<void> {
    const docRef = doc(db, VENDORS_COLLECTION, id);
    const updateData: Record<string, unknown> = { updatedAt: Timestamp.now() };

    Object.keys(updates).forEach(key => {
      if (updates[key as keyof Vendor] !== undefined) {
        updateData[key] = updates[key as keyof Vendor];
      }
    });

    await updateDoc(docRef, updateData);
  }

  static async deleteVendor(id: string): Promise<void> {
    const docRef = doc(db, VENDORS_COLLECTION, id);
    await deleteDoc(docRef);
  }

  // ========== Purchase Orders ==========

  static async getAllPurchaseOrders(): Promise<PurchaseOrder[]> {
    const q = query(
      collection(db, PURCHASE_ORDERS_COLLECTION),
      orderBy('orderDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToPurchaseOrder(doc.id, doc.data()));
  }

  static async getPurchaseOrdersByVendor(vendorId: string): Promise<PurchaseOrder[]> {
    const q = query(
      collection(db, PURCHASE_ORDERS_COLLECTION),
      where('vendorId', '==', vendorId),
      orderBy('orderDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToPurchaseOrder(doc.id, doc.data()));
  }

  static async getPurchaseOrderById(id: string): Promise<PurchaseOrder | null> {
    const docRef = doc(db, PURCHASE_ORDERS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return this.docToPurchaseOrder(docSnap.id, docSnap.data());
  }

  static async createPurchaseOrder(poData: Partial<PurchaseOrder>): Promise<string> {
    const now = Timestamp.now();
    const data: DocumentData = {
      vendorId: poData.vendorId || '',
      vendorName: poData.vendorName || '',
      poNumber: poData.poNumber || '',
      status: poData.status || PurchaseOrderStatus.draft,
      orderDate: poData.orderDate ? Timestamp.fromDate(poData.orderDate) : now,
      expectedDeliveryDate: poData.expectedDeliveryDate ? Timestamp.fromDate(poData.expectedDeliveryDate) : null,
      items: poData.items || [],
      subtotal: poData.subtotal || 0,
      tax: poData.tax || 0,
      shipping: poData.shipping || 0,
      total: poData.total || 0,
      notes: poData.notes || null,
      approvedBy: poData.approvedBy || null,
      approvedAt: poData.approvedAt ? Timestamp.fromDate(poData.approvedAt) : null,
      createdBy: poData.createdBy || '',
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, PURCHASE_ORDERS_COLLECTION), data);
    return docRef.id;
  }

  static async updatePurchaseOrder(id: string, updates: Partial<PurchaseOrder>): Promise<void> {
    const docRef = doc(db, PURCHASE_ORDERS_COLLECTION, id);
    const updateData: Record<string, unknown> = { updatedAt: Timestamp.now() };

    if (updates.vendorId !== undefined) updateData.vendorId = updates.vendorId;
    if (updates.vendorName !== undefined) updateData.vendorName = updates.vendorName;
    if (updates.poNumber !== undefined) updateData.poNumber = updates.poNumber;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.orderDate !== undefined) updateData.orderDate = Timestamp.fromDate(updates.orderDate);
    if (updates.expectedDeliveryDate !== undefined) {
      updateData.expectedDeliveryDate = updates.expectedDeliveryDate ? Timestamp.fromDate(updates.expectedDeliveryDate) : null;
    }
    if (updates.items !== undefined) updateData.items = updates.items;
    if (updates.subtotal !== undefined) updateData.subtotal = updates.subtotal;
    if (updates.tax !== undefined) updateData.tax = updates.tax;
    if (updates.shipping !== undefined) updateData.shipping = updates.shipping;
    if (updates.total !== undefined) updateData.total = updates.total;
    if (updates.notes !== undefined) updateData.notes = updates.notes || null;
    if (updates.approvedBy !== undefined) updateData.approvedBy = updates.approvedBy || null;
    if (updates.approvedAt !== undefined) updateData.approvedAt = updates.approvedAt ? Timestamp.fromDate(updates.approvedAt) : null;

    await updateDoc(docRef, updateData);
  }

  // ========== Invoices ==========

  static async getAllInvoices(): Promise<VendorInvoice[]> {
    const q = query(
      collection(db, VENDOR_INVOICES_COLLECTION),
      orderBy('invoiceDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToInvoice(doc.id, doc.data()));
  }

  static async getInvoicesByVendor(vendorId: string): Promise<VendorInvoice[]> {
    const q = query(
      collection(db, VENDOR_INVOICES_COLLECTION),
      where('vendorId', '==', vendorId),
      orderBy('invoiceDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToInvoice(doc.id, doc.data()));
  }

  static async getInvoiceById(id: string): Promise<VendorInvoice | null> {
    const docRef = doc(db, VENDOR_INVOICES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return this.docToInvoice(docSnap.id, docSnap.data());
  }

  static async createInvoice(invoiceData: Partial<VendorInvoice>): Promise<string> {
    const now = Timestamp.now();
    const data: DocumentData = {
      vendorId: invoiceData.vendorId || '',
      vendorName: invoiceData.vendorName || '',
      invoiceNumber: invoiceData.invoiceNumber || '',
      poNumber: invoiceData.poNumber || null,
      status: invoiceData.status || InvoiceStatus.pending,
      invoiceDate: invoiceData.invoiceDate ? Timestamp.fromDate(invoiceData.invoiceDate) : now,
      dueDate: invoiceData.dueDate ? Timestamp.fromDate(invoiceData.dueDate) : now,
      items: invoiceData.items || [],
      subtotal: invoiceData.subtotal || 0,
      tax: invoiceData.tax || 0,
      total: invoiceData.total || 0,
      amountPaid: invoiceData.amountPaid || 0,
      balance: invoiceData.balance || invoiceData.total || 0,
      paymentDate: invoiceData.paymentDate ? Timestamp.fromDate(invoiceData.paymentDate) : null,
      paymentMethod: invoiceData.paymentMethod || null,
      notes: invoiceData.notes || null,
      createdBy: invoiceData.createdBy || '',
      createdAt: now,
      updatedAt: now,
    };

    const docRef = await addDoc(collection(db, VENDOR_INVOICES_COLLECTION), data);
    return docRef.id;
  }

  static async updateInvoice(id: string, updates: Partial<VendorInvoice>): Promise<void> {
    const docRef = doc(db, VENDOR_INVOICES_COLLECTION, id);
    const updateData: Record<string, unknown> = { updatedAt: Timestamp.now() };

    if (updates.vendorId !== undefined) updateData.vendorId = updates.vendorId;
    if (updates.vendorName !== undefined) updateData.vendorName = updates.vendorName;
    if (updates.invoiceNumber !== undefined) updateData.invoiceNumber = updates.invoiceNumber;
    if (updates.poNumber !== undefined) updateData.poNumber = updates.poNumber || null;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.invoiceDate !== undefined) updateData.invoiceDate = Timestamp.fromDate(updates.invoiceDate);
    if (updates.dueDate !== undefined) updateData.dueDate = Timestamp.fromDate(updates.dueDate);
    if (updates.items !== undefined) updateData.items = updates.items;
    if (updates.subtotal !== undefined) updateData.subtotal = updates.subtotal;
    if (updates.tax !== undefined) updateData.tax = updates.tax;
    if (updates.total !== undefined) updateData.total = updates.total;
    if (updates.amountPaid !== undefined) updateData.amountPaid = updates.amountPaid;
    if (updates.balance !== undefined) updateData.balance = updates.balance;
    if (updates.paymentDate !== undefined) updateData.paymentDate = updates.paymentDate ? Timestamp.fromDate(updates.paymentDate) : null;
    if (updates.paymentMethod !== undefined) updateData.paymentMethod = updates.paymentMethod || null;
    if (updates.notes !== undefined) updateData.notes = updates.notes || null;

    await updateDoc(docRef, updateData);
  }

  // ========== Helper Methods ==========

  private static docToVendor(id: string, data: any): Vendor {
    return {
      id,
      name: data.name || '',
      type: data.type || VendorType.other,
      status: data.status || VendorStatus.pending,
      contactName: data.contactName,
      email: data.email,
      phone: data.phone,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      country: data.country,
      website: data.website,
      taxId: data.taxId,
      paymentTerms: data.paymentTerms,
      rating: data.rating,
      notes: data.notes,
      tags: data.tags || [],
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }

  private static docToPurchaseOrder(id: string, data: any): PurchaseOrder {
    return {
      id,
      vendorId: data.vendorId || '',
      vendorName: data.vendorName || '',
      poNumber: data.poNumber || '',
      status: data.status || PurchaseOrderStatus.draft,
      orderDate: data.orderDate?.toDate() || new Date(),
      expectedDeliveryDate: data.expectedDeliveryDate?.toDate(),
      items: data.items || [],
      subtotal: data.subtotal || 0,
      tax: data.tax || 0,
      shipping: data.shipping || 0,
      total: data.total || 0,
      notes: data.notes,
      approvedBy: data.approvedBy,
      approvedAt: data.approvedAt?.toDate(),
      createdBy: data.createdBy || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }

  private static docToInvoice(id: string, data: any): VendorInvoice {
    return {
      id,
      vendorId: data.vendorId || '',
      vendorName: data.vendorName || '',
      invoiceNumber: data.invoiceNumber || '',
      poNumber: data.poNumber,
      status: data.status || InvoiceStatus.pending,
      invoiceDate: data.invoiceDate?.toDate() || new Date(),
      dueDate: data.dueDate?.toDate() || new Date(),
      items: data.items || [],
      subtotal: data.subtotal || 0,
      tax: data.tax || 0,
      total: data.total || 0,
      amountPaid: data.amountPaid || 0,
      balance: data.balance || data.total || 0,
      paymentDate: data.paymentDate?.toDate(),
      paymentMethod: data.paymentMethod,
      notes: data.notes,
      createdBy: data.createdBy || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
    };
  }
}

